/**
 * signOutWithCleanup — wraps the Cognito signOut so the SWR cache is purged
 * BEFORE the auth state change unmounts the authenticated app shell.
 *
 * Closes the cross-user UI leak (backlog 2026-05-08 URGENT, paired with the
 * user-context architecture spike): without this, User A's data sits in the
 * SWR cache (URL-keyed, no user prefix) and is briefly served to User B on
 * the next sign-in in the same browser tab, before each individual SWR key
 * revalidates.
 *
 * Used by every sign-out entry point — `button-logout.js`, `NavSidebar.js`,
 * `useIdleTimeout.js`. Defense-in-depth: `useCurrentUserContext` ALSO
 * purges on cognito-user-id change at mount time so unusual paths
 * (token-rotation auto-switch, multi-tab scenarios) are also covered.
 *
 * Usage:
 *   const { signOut } = useAuthenticator((c) => [c.signOut]);
 *   const handleLogout = () => signOutWithCleanup(signOut);
 *
 * Or with the direct Amplify import:
 *   import { signOut } from 'aws-amplify/auth';
 *   await signOutWithCleanup(signOut, { global: true });
 *
 * Order matters: clear cache FIRST. A late clear (after signOut) leaves a
 * window where SWR still serves stale data to whatever still mounted.
 * Amplify's signOut flips the auth state and unmounts the authenticated
 * tree, which is what removes the components reading from the cache.
 */

import { mutate } from 'swr';

export default async function signOutWithCleanup(signOutFn, signOutOptions) {
  try {
    // Clear ALL SWR keys without revalidation. The user about to leave
    // doesn't need fresh data; the next user's first render triggers
    // fresh fetches across the board (with their auth + their context).
    mutate(() => true, undefined, { revalidate: false });
  } catch (err) {
    // Cache-clear is best-effort. Even if it fails, signing out is more
    // important than reporting a swallowed clear error to the user.
    // eslint-disable-next-line no-console
    console.warn('signOutWithCleanup: SWR cache clear failed:', err);
  }

  if (typeof signOutFn !== 'function') {
    return;
  }

  // Cognito signOut returns a Promise (Amplify v6+) — await so callers
  // that follow up with a navigate/redirect see the auth state settled.
  // Some signOut paths pass options (e.g. { global: true }) and others
  // pass none — forward whatever the caller gave us.
  if (signOutOptions === undefined) {
    return signOutFn();
  }
  return signOutFn(signOutOptions);
}
